<template>
  <div>
    <ComponentHeader title="Key Metrics Config" subtitle="Configure which key metrics display for specific entities" />

    <div class="p-4 flex gap-4" style="height: 700px;">

      <!-- Left panel: Entity list -->
      <div class="flex flex-col border border-gray-200 rounded-lg overflow-hidden" style="flex: 1; max-width: 860px; min-width: 500px;">

        <!-- Filters -->
        <div class="p-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
          <select
            v-model="filterTypeId"
            class="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          >
            <option :value="null">All Types</option>
            <option v-for="et in entityTypes" :key="et.id" :value="et.id">{{ et.type_name }}</option>
          </select>

          <select
            v-model="filterConfigStatus"
            class="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          >
            <option value="all">All Config Status</option>
            <option value="custom">Has Custom Metrics</option>
            <option value="default">Using Defaults</option>
          </select>

          <input
            v-model="searchQuery" data-demo-search
            type="text"
            placeholder="Search entities..."
            class="sensitive flex-1 text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          />
        </div>

        <!-- Selection toolbar -->
        <div class="px-3 py-2 bg-white border-b border-gray-200 flex items-center gap-3 text-sm">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              :checked="allFilteredSelected"
              :indeterminate="someFilteredSelected && !allFilteredSelected"
              @change="toggleSelectAllFiltered"
              class="rounded border-gray-300 text-blue-dark focus:ring-blue-dark"
            />
            <span class="text-gray-600">Select all ({{ filteredEntities.length }})</span>
          </label>
          <button
            v-if="entitiesWithSameConfig.length > 0 && !selectionHasMixedConfig"
            @click="selectAllWithSameConfig"
            :disabled="selectedEntityIds.length === entitiesWithSameConfig.length"
            :class="[
              'text-xs font-medium',
              selectedEntityIds.length === entitiesWithSameConfig.length
                ? 'text-gray-400 cursor-default'
                : 'text-blue-dark hover:text-blue-800'
            ]"
          >
            {{ selectedEntityIds.length === entitiesWithSameConfig.length ? 'All similar selected' : `Select all with same config (${entitiesWithSameConfig.length})` }}
          </button>
          <span v-if="selectedEntityIds.length > 0" class="ml-auto text-gray-500">
            {{ selectedEntityIds.length }} selected
          </span>
        </div>

        <!-- Entity table -->
        <div class="flex-1 overflow-y-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="w-10 px-3 py-2"></th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase" style="width: 100px;">Type</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase" style="min-width: 300px;">Metric Config</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-if="loading" class="text-center">
                <td colspan="4" class="px-3 py-8 text-sm text-gray-500">
                  <font-awesome-icon :icon="['fas', 'spinner']" spin class="mr-2" />Loading entities...
                </td>
              </tr>
              <tr v-else-if="filteredEntities.length === 0">
                <td colspan="4" class="px-3 py-8 text-sm text-gray-500 text-center">No entities match filters</td>
              </tr>
              <tr
                v-for="entity in filteredEntities"
                :key="entity.id"
                :class="[
                  'hover:bg-gray-50 cursor-pointer transition-colors',
                  isSelected(entity.id) ? 'bg-blue-50' : ''
                ]"
                @click="toggleSelect(entity.id)"
              >
                <td class="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    :checked="isSelected(entity.id)"
                    class="rounded border-gray-300 text-blue-dark focus:ring-blue-dark pointer-events-none"
                  />
                </td>
                <td class="sensitive px-3 py-2 text-sm font-medium text-gray-900" :data-spoof-kind="entitySpoofKind(entity.entity_type_name, entity.name)">{{ entity.name }}</td>
                <td class="px-3 py-2 text-sm text-gray-500">{{ entity.entity_type_name }}</td>
                <td class="px-3 py-2">
                  <div v-if="getEntityMetricIds(entity).length > 0" class="flex flex-wrap gap-1.5">
                    <span
                      v-for="mid in getEntityMetricIds(entity).slice(0, 3)"
                      :key="mid"
                      class="inline-flex items-center bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-xs font-medium"
                    >
                      {{ metricNames[mid] || `#${mid}` }}
                    </span>
                    <span
                      v-if="getEntityMetricIds(entity).length > 3"
                      class="text-xs text-gray-400"
                    >
                      +{{ getEntityMetricIds(entity).length - 3 }} more
                    </span>
                  </div>
                  <span v-else class="text-xs text-gray-400 italic">Using defaults</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Right panel: Metric config -->
      <div class="w-96 flex flex-col border border-gray-200 rounded-lg overflow-hidden self-start sticky top-4" style="max-height: 680px;">

        <!-- No selection state -->
        <div v-if="selectedEntityIds.length === 0" class="flex items-center justify-center p-6" style="min-height: 300px;">
          <div class="text-center text-gray-400">
            <font-awesome-icon :icon="['fas', 'gear']" class="text-3xl mb-3" />
            <p class="text-sm">Select entities to configure their scorecard metrics</p>
          </div>
        </div>

        <!-- Mixed config state -->
        <template v-else-if="selectionHasMixedConfig">
          <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div class="text-sm font-semibold text-gray-900">
              {{ selectedEntityIds.length }} entities selected
            </div>
          </div>
          <div class="flex-1 flex items-center justify-center p-6">
            <div class="text-center">
              <div class="text-amber-600 mb-3">
                <font-awesome-icon :icon="['fas', 'info-circle']" class="text-2xl" />
              </div>
              <p class="text-sm font-medium text-gray-900 mb-1">Different configurations selected</p>
              <p class="text-xs text-gray-500 mb-4">Select entities with the same config, or clear all overrides to start fresh.</p>
              <button
                v-if="selectionHasConfig"
                @click="clearConfig"
                :disabled="saving"
                class="btn-danger-sm"
              >
                <font-awesome-icon :icon="['fas', 'trash']" class="w-3 h-3" />
                Clear All Overrides
              </button>
            </div>
          </div>
        </template>

        <!-- Uniform config panel -->
        <template v-else>
          <!-- Header -->
          <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div class="text-sm font-semibold text-gray-900">
              {{ selectedEntityIds.length }} {{ selectedEntityIds.length === 1 ? 'entity' : 'entities' }} selected
            </div>
            <div v-if="selectionHasConfig" class="mt-1 text-xs text-blue-dark flex items-center gap-1">
              <font-awesome-icon :icon="['fas', 'info-circle']" class="w-3 h-3" />
              All selected entities share the same custom config
            </div>
            <div v-else class="mt-1 text-xs text-gray-500">
              All selected entities are using defaults
            </div>
          </div>

          <!-- Current/pending pills -->
          <div v-if="pendingMetricIds.length > 0" class="px-4 py-2 border-b border-gray-200">
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="mid in pendingMetricIds"
                :key="'pill-' + mid"
                class="inline-flex items-center gap-1 bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-xs font-medium"
              >
                {{ metricNames[mid] || `Metric ${mid}` }}
                <button @click="toggleMetric(mid)" class="ml-0.5 text-blue-600 hover:text-blue-900">
                  <font-awesome-icon :icon="['fas', 'times']" class="w-2.5 h-2.5" />
                </button>
              </span>
            </div>
          </div>

          <!-- Search -->
          <div class="px-4 pt-3">
            <input
              v-model="metricSearch"
              type="text"
              placeholder="Search metrics..."
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
            />
          </div>

          <!-- Metric list -->
          <div class="flex-1 overflow-y-auto px-4 py-3">
            <div
              v-for="mid in filteredMetricList"
              :key="mid"
              :class="[
                'flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors',
                isMetricSelected(mid)
                  ? 'bg-blue-50 border border-blue-200'
                  : 'border border-transparent hover:bg-gray-50'
              ]"
              @click="toggleMetric(mid)"
            >
              <input
                type="checkbox"
                :checked="isMetricSelected(mid)"
                class="rounded border-gray-300 text-blue-dark focus:ring-blue-dark pointer-events-none"
              />
              <span :class="['text-sm', isMetricSelected(mid) ? 'text-gray-900 font-medium' : 'text-gray-700']">
                {{ metricNames[mid] || `Metric ${mid}` }}
              </span>
            </div>
          </div>

          <!-- Footer actions -->
          <div class="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <button
              v-if="selectionHasConfig || pendingMetricIds.length > 0"
              @click="clearConfig"
              :disabled="saving"
              class="btn-danger-sm"
            >
              <font-awesome-icon :icon="['fas', 'trash']" class="w-3 h-3" />
              Clear Override
            </button>
            <span v-else></span>
            <button
              @click="saveConfig"
              :disabled="saving || pendingMetricIds.length === 0"
              class="btn-primary-sm"
            >
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faGear, faSpinner, faInfoCircle, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import demoProtectedFields from '@/mixins/demoProtectedFields';
import { entitySpoofKind } from '@/utils/demoSpoof';

library.add(faGear, faSpinner, faInfoCircle, faTimes, faTrash);

export default {
  name: 'EntityScorecardMetrics',
  components: { FontAwesomeIcon, ComponentHeader },
  mixins: [demoProtectedFields],
  // The search filters entity rows whose names are tagged. Demo Mode empties it
  // and the tagged input is read-only. It is not persisted.
  demoProtectedFields: ['searchQuery'],
  data() {
    return {
      loading: false,
      saving: false,
      entities: [],
      entityTypes: [],
      allMetricIds: [],
      metricNames: {},
      filterTypeId: null,
      filterConfigStatus: 'all',
      searchQuery: '',
      selectedEntityIds: [],
      pendingMetricIds: [],
      metricSearch: '',
    };
  },
  computed: {
    filteredEntities() {
      return this.entities.filter(e => {
        if (this.filterTypeId && e.entity_type !== this.filterTypeId) return false;
        if (this.searchQuery && !e.name.toLowerCase().includes(this.searchQuery.toLowerCase())) return false;
        if (this.filterConfigStatus === 'custom' && !this.getEntityMetricIds(e).length) return false;
        if (this.filterConfigStatus === 'default' && this.getEntityMetricIds(e).length) return false;
        return true;
      });
    },

    allFilteredSelected() {
      return this.filteredEntities.length > 0 && this.filteredEntities.every(e => this.selectedEntityIds.includes(e.id));
    },

    someFilteredSelected() {
      return this.filteredEntities.some(e => this.selectedEntityIds.includes(e.id));
    },

    selectedEntities() {
      return this.entities.filter(e => this.selectedEntityIds.includes(e.id));
    },

    selectionConfigs() {
      // Get unique config signatures for selected entities
      return this.selectedEntities.map(e => {
        const ids = this.getEntityMetricIds(e);
        return ids.length ? JSON.stringify(ids) : null;
      });
    },

    selectionHasConfig() {
      return this.selectionConfigs.some(c => c !== null);
    },

    selectionHasMixedConfig() {
      const unique = [...new Set(this.selectionConfigs)];
      return unique.length > 1;
    },

    entitiesWithSameConfig() {
      if (this.selectedEntityIds.length === 0 || this.selectionHasMixedConfig) return [];
      const selected = this.selectedEntities[0];
      const selectedSig = JSON.stringify(this.getEntityMetricIds(selected));
      return this.filteredEntities.filter(e => JSON.stringify(this.getEntityMetricIds(e)) === selectedSig);
    },

    filteredMetricList() {
      if (!this.metricSearch) return this.allMetricIds;
      const q = this.metricSearch.toLowerCase();
      return this.allMetricIds.filter(mid =>
        (this.metricNames[mid] || '').toLowerCase().includes(q)
      );
    },
  },

  watch: {
    selectedEntityIds: {
      handler() {
        this.syncPendingFromSelection();
      },
      deep: true,
    },
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    entitySpoofKind,
    async fetchData() {
      this.loading = true;
      try {
        // Patients never carry scorecard config and make up most of a tenant's
        // entities (150K+ on IHCI). /api/entities/ is unpaginated, so including
        // them loads the whole tenant.
        const [entitiesRes, typesRes, domainsRes] = await Promise.all([
          axios.get('/api/entities/', { params: { exclude_type: 'Patient' } }),
          axios.get('/api/entity-types/'),
          axios.get('/api/performancedomains/'),
        ]);

        this.entities = (entitiesRes.data.results || entitiesRes.data).map(e => ({
          id: e.id,
          name: e.name,
          entity_type: e.entity_type,
          entity_type_name: e.entity_type_name,
          display_config: e.display_config || {},
        }));

        this.entityTypes = typesRes.data.filter(t => t.is_active && t.type_name !== 'Patient');

        // Build metric catalog from performance domains
        const ids = [];
        const names = {};
        for (const domain of domainsRes.data) {
          for (const cm of (domain.composable_metrics || [])) {
            if (cm.metric_id && !ids.includes(cm.metric_id)) {
              ids.push(cm.metric_id);
            }
            if (cm.metric_name) {
              names[cm.metric_id] = cm.metric_name;
            }
          }
        }
        this.allMetricIds = ids;
        this.metricNames = names;
      } catch (e) {
        console.error('Failed to load data:', e);
      } finally {
        this.loading = false;
      }
    },

    getEntityMetricIds(entity) {
      return entity.display_config?.scorecard_metric_ids || [];
    },

    isSelected(entityId) {
      return this.selectedEntityIds.includes(entityId);
    },

    toggleSelect(entityId) {
      const idx = this.selectedEntityIds.indexOf(entityId);
      if (idx >= 0) {
        this.selectedEntityIds.splice(idx, 1);
      } else {
        this.selectedEntityIds.push(entityId);
      }
    },

    toggleSelectAllFiltered() {
      if (this.allFilteredSelected) {
        const filteredIds = new Set(this.filteredEntities.map(e => e.id));
        this.selectedEntityIds = this.selectedEntityIds.filter(id => !filteredIds.has(id));
      } else {
        const current = new Set(this.selectedEntityIds);
        for (const e of this.filteredEntities) {
          current.add(e.id);
        }
        this.selectedEntityIds = [...current];
      }
    },

    selectAllWithSameConfig() {
      this.selectedEntityIds = this.entitiesWithSameConfig.map(e => e.id);
    },

    syncPendingFromSelection() {
      this.metricSearch = '';
      if (this.selectedEntityIds.length === 0) {
        this.pendingMetricIds = [];
        return;
      }
      if (this.selectionHasMixedConfig) {
        this.pendingMetricIds = [];
        return;
      }
      // All same config — load it
      const first = this.selectedEntities[0];
      this.pendingMetricIds = [...this.getEntityMetricIds(first)];
    },

    isMetricSelected(mid) {
      return this.pendingMetricIds.includes(mid);
    },

    toggleMetric(mid) {
      const idx = this.pendingMetricIds.indexOf(mid);
      if (idx >= 0) {
        this.pendingMetricIds.splice(idx, 1);
      } else {
        this.pendingMetricIds.push(mid);
      }
    },

    async saveConfig() {
      if (this.pendingMetricIds.length === 0) return;
      this.saving = true;
      try {
        await axios.post('/api/entities/bulk-update-display-config/', {
          entity_ids: this.selectedEntityIds,
          action: 'set',
          scorecard_metric_ids: this.pendingMetricIds,
        });
        // Update local state
        for (const entity of this.selectedEntities) {
          entity.display_config = {
            ...entity.display_config,
            scorecard_metric_ids: [...this.pendingMetricIds],
          };
        }
      } catch (e) {
        console.error('Failed to save:', e);
      } finally {
        this.saving = false;
      }
    },

    async clearConfig() {
      this.saving = true;
      try {
        await axios.post('/api/entities/bulk-update-display-config/', {
          entity_ids: this.selectedEntityIds,
          action: 'clear',
        });
        // Update local state
        for (const entity of this.selectedEntities) {
          const config = { ...entity.display_config };
          delete config.scorecard_metric_ids;
          entity.display_config = config;
        }
        this.pendingMetricIds = [];
      } catch (e) {
        console.error('Failed to clear:', e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
